import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/axiosConfig";
import localforage from "localforage";

const API_URL = process.env.REACT_APP_API_URL;
const MUTED = "#6b7a8d";
const BLUE = "#2563eb";
const RED = "#e5484d";

/**
 * Full-screen embed for an externally hosted dashboard.
 *
 *   /dashboard-embed/:key      (key registered in routes/dashboards.js)
 *
 * The API decides whether the user may open it and returns the URL, title and
 * the project hub to return to. The dashboard fills the whole viewport; a small
 * floating bar offers Back / Reload / Open in new tab and fades out of the way.
 */
const EmbeddedDashboard = ({ dashKey }) => {
  const params = useParams();
  const navigate = useNavigate();
  const key = dashKey || params.key;

  const [state, setState] = useState("checking"); // checking | ok | denied | missing | error
  const [info, setInfo] = useState(null);
  const [hub, setHub] = useState("/leadership-dashboards");
  const [loaded, setLoaded] = useState(false);
  const [slow, setSlow] = useState(false);
  const [showBar, setShowBar] = useState(true);
  const frameRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setState("checking"); setLoaded(false); setSlow(false); setShowBar(true);
    (async () => {
      try {
        const token = await localforage.getItem("token");
        const r = await api.get(`${API_URL}/dashboards/embed-access/${encodeURIComponent(key)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!alive) return;
        setInfo(r.data);
        if (r.data.hub) setHub(r.data.hub);
        document.title = `${r.data.title} · STC Workplace`;
        setState("ok");
      } catch (e) {
        if (!alive) return;
        const st = e.response?.status;
        if (e.response?.data?.hub) setHub(e.response.data.hub);
        setState(st === 403 ? "denied" : st === 404 ? "missing" : "error");
      }
    })();
    return () => { alive = false; };
  }, [key]);

  // fade the controls after a moment; they return on hover
  useEffect(() => {
    if (state !== "ok") return undefined;
    const t = setTimeout(() => setShowBar(false), 4000);
    const s = setTimeout(() => setSlow(true), 7000);
    return () => { clearTimeout(t); clearTimeout(s); };
  }, [state]);

  // keyboard: Esc goes back
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") navigate(hub); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hub, navigate]);

  const goBack = () => navigate(hub);
  const openNewTab = () => info && window.open(info.url, "_blank", "noopener,noreferrer");
  const reload = () => {
    setLoaded(false); setSlow(false);
    if (frameRef.current && info) frameRef.current.src = info.url;
  };

  const btn = { border: "none", borderRadius: 8, padding: "7px 13px", fontSize: 12.5,
                fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" };

  if (state !== "ok") {
    const msg = {
      checking: "Checking access…",
      denied: "You do not have access to this dashboard.",
      missing: "This dashboard could not be found.",
      error: "Could not verify access. Please refresh, or contact the admin.",
    }[state];
    return (
      <div style={{ position: "fixed", inset: 0, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center", gap: 14,
                    background: "#f7f9fc", padding: 20, textAlign: "center" }}>
        <div style={{ color: state === "checking" ? MUTED : RED, fontWeight: 600, fontSize: 15 }}>{msg}</div>
        {state !== "checking" && (
          <button type="button" onClick={goBack} style={{ ...btn, background: BLUE, color: "#fff" }}>
            ← Back to Leadership Dashboards
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "#fff", overflow: "hidden" }}>
      {!loaded && (
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center", gap: 10,
                      background: "#fff", zIndex: 2, textAlign: "center", padding: 20 }}>
          <div style={{ color: MUTED, fontSize: 14 }}>Loading {info.title}…</div>
          {slow && (
            <div style={{ maxWidth: 460, fontSize: 13, color: MUTED }}>
              Still loading. If nothing appears, the dashboard may not allow embedding —{" "}
              <button type="button" onClick={openNewTab}
                style={{ background: "none", border: "none", color: BLUE, fontWeight: 700,
                         cursor: "pointer", fontSize: 13, textDecoration: "underline", padding: 0 }}>
                open it in a new tab
              </button>.
            </div>
          )}
        </div>
      )}

      <iframe
        key={key}
        ref={frameRef}
        title={info.title}
        src={info.url}
        onLoad={() => setLoaded(true)}
        style={{ width: "100%", height: "100%", border: "none", display: "block" }}
        allow="fullscreen; geolocation; clipboard-read; clipboard-write"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
      />

      {info.controls !== false && (
      <div
        onMouseEnter={() => setShowBar(true)}
        onMouseLeave={() => setShowBar(false)}
        style={{
          position: "absolute", top: 10, right: 12, zIndex: 3,
          display: "flex", gap: 8, alignItems: "center",
          background: "rgba(255,255,255,0.95)", border: "1px solid #e6ecf3",
          borderRadius: 10, padding: "6px 8px", boxShadow: "0 3px 12px rgba(0,0,0,.14)",
          opacity: showBar ? 1 : 0.22, transition: "opacity .25s ease",
        }}
      >
        <button type="button" onClick={goBack} title="Back to Leadership Dashboards (Esc)"
          style={{ ...btn, background: "#eef2f7", color: "#1e2a3a" }}>
          ← Back
        </button>
        <button type="button" onClick={reload} style={{ ...btn, background: "#eef2f7", color: "#1e2a3a" }}>
          Reload
        </button>
        <button type="button" onClick={openNewTab} style={{ ...btn, background: BLUE, color: "#fff" }}>
          Open in new tab
        </button>
      </div>
      )}
    </div>
  );
};

export default EmbeddedDashboard;
